import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router'
import { rpc, useRpcStatus } from '../api/rpc'
import { session, useSession } from '../api/session'
import type { ChatSummary } from '../api/types'
import { cn } from '../lib/cn'
import { useResolvedTheme, useStatusBarDim, useThemePref } from '../lib/theme'
import { ChatSearchModal } from './chat/ChatSearchModal'
import { groupChatsForSidebar } from './chatListUtils'
import { IconBolt, IconChat, IconChevronDown, IconChevronRight, IconClose, IconEdit, IconFolder, IconGear, IconInbox, IconLogo, IconLogout, IconMenu, IconMoon, IconMore, IconPanelLeft, IconPin, IconPinOff, IconPlus, IconSearch, IconSun, IconTrash, IconUser } from './icons'
import { confirmDialog, promptDialog } from './ui/Confirm'
import { Dropdown } from './ui/Popover'
import { useChatList } from './useChatList'
import { useInboxCounts } from './useInboxCounts'

interface MobileBar { title?: string; actions?: ReactNode }
const MobileBarCtx = createContext<(bar: MobileBar | null) => void>(() => {})

/**
 * Lets a page put its title and actions in the phone top bar (so phones show one header, not two).
 * `deps` decides when the bar is refreshed.
 */
export function useMobileBar(bar: MobileBar, deps: unknown[]): void {
  const set = useContext(MobileBarCtx)
  useEffect(() => {
    set(bar)
    return () => set(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

function NavItem({ to, label, icon, count, active, onNavigate, collapsed, className }: {
  to: string; label: string; icon: ReactNode; count?: number; active?: boolean; onNavigate(): void; collapsed?: boolean; className?: string
}) {
  const { pathname } = useLocation()
  const isActive = active ?? (pathname === to || pathname.startsWith(`${to}/`))
  return (
    <Link
      to={to} onClick={onNavigate}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'relative flex items-center rounded-[8px] font-medium transition-colors',
        collapsed
          ? 'mx-auto h-11 w-11 justify-center'
          : 'h-9 w-full gap-2.5 px-2.5 text-sm max-md:h-11',
        isActive ? 'bg-ink-100/[0.07] text-ink-100' : 'text-ink-300 hover:bg-cream-200 hover:text-ink-100',
        className
      )}
    >
      <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center">{icon}</span>
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
      {count ? (
        collapsed ? (
          <span className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-amber-50 px-1 text-[10px] font-bold text-amber-700">
            {count > 99 ? '99+' : count}
          </span>
        ) : (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-50 px-[7px] text-xs font-medium text-amber-700">{count}</span>
        )
      ) : null}
    </Link>
  )
}

/** Light/dark switch for the sidebar footer; the three-way choice (incl. "system") lives in Settings. */
function ThemeSwitch({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation()
  const [, setPref] = useThemePref()
  const resolved = useResolvedTheme()
  if (collapsed) {
    const next = resolved === 'dark' ? 'light' : 'dark'
    return (
      <button
        type="button"
        onClick={() => setPref(next)}
        title={t(`theme.${next}`)}
        aria-label={t(`theme.${next}`)}
        className="mx-auto flex h-11 w-11 items-center justify-center rounded-[8px] text-ink-500 transition-colors hover:bg-cream-200 hover:text-ink-100"
      >
        {resolved === 'dark' ? <IconSun width={18} height={18} /> : <IconMoon width={18} height={18} />}
      </button>
    )
  }
  return (
    <div role="group" aria-label={t('theme.label')} className="flex shrink-0 items-center gap-0.5 rounded-[8px] bg-cream-200 p-0.5">
      {(['light', 'dark'] as const).map(mode => {
        const on = resolved === mode
        return (
          <button
            key={mode}
            type="button"
            onClick={() => setPref(mode)}
            title={t(`theme.${mode}`)}
            aria-label={t(`theme.${mode}`)}
            aria-pressed={on}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-[6px] transition-colors max-md:h-11 max-md:w-11',
              on ? 'bg-cream-50 text-ink-100 shadow-[0_0_0_1px_var(--color-line)]' : 'text-ink-500 hover:text-ink-100'
            )}
          >
            {mode === 'light' ? <IconSun width={15} height={15} /> : <IconMoon width={15} height={15} />}
          </button>
        )
      })}
    </div>
  )
}

/** Title shown in the mobile top bar for the current route. */
function useRouteTitle(): string {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  if (pathname.startsWith('/automations')) return t('nav.automations')
  if (pathname.startsWith('/inbox')) return t('nav.inbox')
  if (pathname.startsWith('/files')) return t('nav.files')
  if (pathname.startsWith('/settings')) return t('nav.settings')
  return 'Meow'
}

export function AppShell() {
  const { t } = useTranslation()
  const { user } = useSession()
  const status = useRpcStatus()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { chatId } = useParams()
  const [open, setOpen] = useState(false)
  // The phone drawer's scrim dims the page; the status bar follows it (lib/theme.ts).
  useStatusBarDim(open)
  const [searchOpen, setSearchOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar_collapsed') === '1'
    } catch {
      return false
    }
  })
  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev
      try {
        localStorage.setItem('sidebar_collapsed', next ? '1' : '0')
      } catch {}
      return next
    })
  }
  const [bar, setBar] = useState<MobileBar | null>(null)
  const { items } = useChatList()
  const inbox = useInboxCounts()
  const title = useRouteTitle()
  const close = () => setOpen(false)
  useEffect(() => { setOpen(false) }, [pathname])
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])
  const inChat = pathname === '/' || pathname.startsWith('/chat/')
  const initials = (user?.name ?? '?').split(/\s+/).map(s => s[0]).slice(-2).join('').toUpperCase()

  const { pinned, repoGroups, recent } = useMemo(() => groupChatsForSidebar(items), [items])

  const [collapsedRepos, setCollapsedRepos] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem('sidebar_collapsed_repos')
      return raw ? JSON.parse(raw) : {}
    } catch {
      return {}
    }
  })

  const toggleRepoCollapsed = (repoId: string) => {
    setCollapsedRepos(prev => {
      const next = { ...prev, [repoId]: !prev[repoId] }
      try {
        localStorage.setItem('sidebar_collapsed_repos', JSON.stringify(next))
      } catch {
        // ignore
      }
      return next
    })
  }

  const handleRenameChat = async (c: ChatSummary) => {
    const title = await promptDialog(t('chat.renamePrompt'), {
      defaultValue: c.title === 'New chat' ? '' : c.title,
      confirmLabel: t('common.save')
    })
    if (title && title.trim()) {
      await rpc.call('chats.rename', { chatId: c.id, title: title.trim() })
    }
  }

  const handlePinChat = async (c: ChatSummary) => {
    await rpc.call('chats.pin', { chatId: c.id, pinned: !c.pinnedAt })
  }

  const handleDeleteChat = async (c: ChatSummary) => {
    if (!await confirmDialog(t('chat.confirmDelete'))) return
    await rpc.call('chats.delete', { chatId: c.id })
    if (chatId === c.id) {
      navigate('/')
    }
  }

  const sidebar = (mobile: boolean) => {
    const isCollapsed = !mobile && collapsed

    const renderChatItem = (c: ChatSummary, showRepoBadge?: boolean) => {
      const isCurrent = c.id === chatId
      return (
        <div key={c.id} className="group relative flex items-center">
          <NavLink
            to={`/chat/${c.id}`}
            onClick={close}
            className={cn(
              'flex min-w-0 flex-1 items-center gap-2 rounded-[8px] px-2.5 py-1.5 text-[13.5px] max-md:py-2.5 md:pr-8',
              isCurrent ? 'bg-ink-100/[0.07] font-medium text-ink-100' : 'text-ink-300 hover:bg-cream-200 hover:text-ink-100'
            )}
          >
            {c.running && <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-ginger-500" />}
            <span className="truncate">{c.title === 'New chat' ? t('chat.untitled') : c.title}</span>
            {showRepoBadge && c.repoName && (
              <span
                className="ml-auto shrink-0 max-w-[80px] truncate rounded bg-cream-200 px-1.5 py-0.5 font-mono text-[10px] text-ink-500"
                title={c.repoOwner ? `${c.repoOwner}/${c.repoName}` : c.repoName}
              >
                {c.repoName}
              </span>
            )}
          </NavLink>
          <div className="absolute right-1 top-1/2 -translate-y-1/2 max-md:hidden">
            <Dropdown
              label={t('common.more')}
              items={[
                {
                  label: t('chat.rename'),
                  icon: <IconEdit width={15} height={15} />,
                  onSelect: () => void handleRenameChat(c)
                },
                {
                  label: c.pinnedAt ? t('chat.unpin') : t('chat.pin'),
                  icon: c.pinnedAt ? <IconPinOff width={15} height={15} /> : <IconPin width={15} height={15} />,
                  onSelect: () => void handlePinChat(c)
                },
                {
                  label: t('common.delete'),
                  icon: <IconTrash width={15} height={15} />,
                  danger: true,
                  divider: true,
                  onSelect: () => void handleDeleteChat(c)
                }
              ]}
              trigger={p => (
                <button
                  {...p}
                  ref={p.ref}
                  type="button"
                  aria-label={t('common.more')}
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-[6px] text-ink-500 transition-opacity hover:bg-cream-300/60 hover:text-ink-100',
                    p['aria-expanded'] ? 'bg-cream-300/60 text-ink-100 opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
                  )}
                >
                  <IconMore width={14} height={14} />
                </button>
              )}
            />
          </div>
        </div>
      )
    }

    return (
      <aside
        className={cn(
          'flex h-full flex-col border-r border-line bg-sidebar text-ink-300 transition-[width] duration-200',
          isCollapsed ? 'w-16 gap-3 px-2' : 'w-[264px] gap-3 px-3 max-md:w-[312px]',
          mobile
            ? 'pt-[calc(0.75rem+env(safe-area-inset-top,0px))] pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] pl-[calc(0.75rem+env(safe-area-inset-left,0px))] pr-[calc(0.75rem+env(safe-area-inset-right,0px))]'
            : 'py-3'
        )}
      >
        {isCollapsed ? (
          <div className="flex h-10 items-center justify-center">
            <button
              type="button"
              onClick={toggleCollapsed}
              title={t('nav.expandSidebar')}
              aria-label={t('nav.expandSidebar')}
              className="flex h-10 w-10 items-center justify-center rounded-[8px] text-ink-500 transition-colors hover:bg-cream-200 hover:text-ink-100"
            >
              <IconPanelLeft width={18} height={18} />
            </button>
          </div>
        ) : (
          <div className="flex h-10 shrink-0 items-center gap-2.5 px-1">
            <IconLogo className="h-7 w-7 shrink-0 rounded-[8px] object-cover" />
            <span className="font-display text-base font-bold tracking-[-0.01em] text-ink-100">Meow</span>
            <span
              role="status"
              title={t(`status.${status}`)}
              aria-label={t(`status.${status}`)}
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', status === 'open' ? 'bg-mint-500' : status === 'offline' ? 'bg-red-600' : 'bg-amber-400')}
            />
            {!mobile && (
              <button
                type="button"
                onClick={toggleCollapsed}
                title={t('nav.collapseSidebar')}
                aria-label={t('nav.collapseSidebar')}
                className="ml-auto flex h-[30px] w-[30px] items-center justify-center rounded-[7px] text-ink-500 transition-colors hover:bg-cream-200 hover:text-ink-100"
              >
                <IconPanelLeft width={16} height={16} />
              </button>
            )}
            {mobile && (
              <button
                type="button"
                aria-label={t('common.close')}
                onClick={close}
                className="ml-auto flex h-11 w-11 items-center justify-center rounded-[8px] text-ink-300 hover:bg-cream-200 hover:text-ink-100"
              >
                <IconClose />
              </button>
            )}
          </div>
        )}

        {isCollapsed ? (
          <button
            type="button"
            onClick={() => { close(); navigate('/') }}
            title={t('chat.new')}
            aria-label={t('chat.new')}
            className="mx-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-ginger-500 text-white transition-colors hover:bg-ginger-400"
          >
            <IconPlus width={20} height={20} />
          </button>
        ) : (
          <div className="flex shrink-0 gap-1.5">
            <button
              type="button"
              onClick={() => { close(); navigate('/') }}
              className="flex h-9 flex-1 items-center justify-start gap-2 rounded-[8px] bg-ginger-500 px-3 text-sm font-semibold text-white transition-colors hover:bg-ginger-400 max-md:h-11"
            >
              <IconPlus width={16} height={16} />{t('chat.new')}
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              title={`${t('chat.search')} (Ctrl K)`}
              aria-label={`${t('chat.search')} (Ctrl K)`}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-line bg-cream-50 text-ink-300 transition-colors hover:bg-cream-200 hover:text-ink-100 max-md:h-11 max-md:w-11"
            >
              <IconSearch width={16} height={16} />
            </button>
          </div>
        )}

        {isCollapsed && (
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            title={`${t('chat.search')} (Ctrl K)`}
            aria-label={`${t('chat.search')} (Ctrl K)`}
            className="mx-auto flex h-11 w-11 items-center justify-center rounded-[8px] text-ink-500 transition-colors hover:bg-cream-200 hover:text-ink-100"
          >
            <IconSearch width={18} height={18} />
          </button>
        )}

        <nav aria-label={t('nav.main')} className="flex flex-col gap-0.5">
          <NavItem to="/" label={t('nav.chat')} icon={<IconChat />} active={inChat} onNavigate={close} collapsed={isCollapsed} />
          <NavItem to="/automations" label={t('nav.automations')} icon={<IconBolt />} onNavigate={close} collapsed={isCollapsed} />
          <NavItem to="/inbox" label={t('nav.inbox')} icon={<IconInbox />} count={inbox.approvals + inbox.notifications} onNavigate={close} collapsed={isCollapsed} />
          <NavItem to="/files" label={t('nav.files')} icon={<IconFolder />} onNavigate={close} collapsed={isCollapsed} />
        </nav>

        {!isCollapsed ? (
          <nav aria-label={t('chat.history')} className="scrollbar-sidebar -mr-3 flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto pr-1.5">
            {pinned.length > 0 && (
              <div className="mb-2 flex flex-col gap-0.5">
                <div className="px-2.5 pb-1 text-xs font-medium text-ink-500">{t('nav.pinned')}</div>
                {pinned.map(c => renderChatItem(c, true))}
              </div>
            )}

            {repoGroups.length > 0 && (
              <div className="mb-2 flex flex-col gap-0.5">
                <div className="px-2.5 pb-1 text-xs font-medium text-ink-500">{t('nav.repositories')}</div>
                {repoGroups.map(group => {
                  const isFolded = Boolean(collapsedRepos[group.repoId] && !group.chats.some(c => c.id === chatId))
                  return (
                    <div key={group.repoId} className="flex flex-col">
                      <div className="group/repo flex items-center justify-between rounded-[8px] px-2 py-1 text-ink-300 transition-colors hover:bg-cream-200 hover:text-ink-100">
                        <button
                          type="button"
                          onClick={() => toggleRepoCollapsed(group.repoId)}
                          className="flex min-w-0 flex-1 items-center gap-1.5 text-left font-medium"
                          aria-expanded={!isFolded}
                          title={group.repoOwner ? `${group.repoOwner}/${group.repoName}` : group.repoName}
                        >
                          <span className="shrink-0 text-ink-500 group-hover/repo:text-ink-300">
                            {isFolded ? <IconChevronRight width={12} height={12} /> : <IconChevronDown width={12} height={12} />}
                          </span>
                          <span className="truncate font-mono text-[11.5px]">{group.repoName}</span>
                          <span className="ml-1 inline-flex shrink-0 items-center rounded-[5px] bg-cream-200 px-1.5 py-0.5 text-[10px] font-semibold text-ink-500 group-hover/repo:bg-cream-300/60 group-hover/repo:text-ink-300">
                            {group.chats.length}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            close()
                            const q = new URLSearchParams()
                            q.set('repoId', group.repoId)
                            if (group.workspaceId) q.set('workspaceId', group.workspaceId)
                            navigate(`/?${q.toString()}`)
                          }}
                          title={t('chat.newInRepo', { repo: group.repoName })}
                          aria-label={t('chat.newInRepo', { repo: group.repoName })}
                          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] text-ink-500 opacity-0 transition-opacity hover:bg-cream-300/60 hover:text-ink-100 focus:opacity-100 group-hover/repo:opacity-100"
                        >
                          <IconPlus width={12} height={12} />
                        </button>
                      </div>
                      {!isFolded && (
                        <div className="my-0.5 ml-3 flex flex-col gap-0.5 border-l border-line pl-2">
                          {group.chats.map(c => renderChatItem(c, false))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {(recent.length > 0 || (pinned.length === 0 && repoGroups.length === 0)) && (
              <div className="flex flex-col gap-0.5">
                <div className="px-2.5 pb-1 text-xs font-medium text-ink-500">{t('nav.recent')}</div>
                {items.length === 0 && <p className="px-2.5 py-1 text-xs text-ink-500">{t('chat.noChats')}</p>}
                {recent.map(c => renderChatItem(c, false))}
              </div>
            )}
          </nav>
        ) : (
          <div className="flex-1" />
        )}

        <div className="flex flex-col gap-1 border-t border-line pt-2">
          {isCollapsed ? (
            <NavItem to="/settings" label={t('nav.settings')} icon={<IconGear />} onNavigate={close} collapsed />
          ) : (
            <div className="flex items-center gap-1.5">
              <NavItem to="/settings" label={t('nav.settings')} icon={<IconGear />} onNavigate={close} className="flex-1" />
              <ThemeSwitch collapsed={false} />
            </div>
          )}
          <div className={cn('flex items-center', isCollapsed ? 'justify-center pt-1' : 'gap-2.5 px-1.5 pt-1')}>
            {isCollapsed ? (
              <>
                <ThemeSwitch collapsed />
                <Dropdown
                  label={t('nav.account')}
                  items={[
                    { label: t('settings.profile'), icon: <IconUser width={16} height={16} />, onSelect: () => navigate('/settings/profile') },
                    { label: t('auth.logout'), icon: <IconLogout width={16} height={16} />, onSelect: () => void session.logout(), divider: true }
                  ]}
                  trigger={p => (
                    <button
                      {...p}
                      ref={p.ref}
                      type="button"
                      aria-label={t('nav.account')}
                      title={user?.name ?? t('nav.account')}
                      className="relative mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-ginger-100 text-[13px] font-semibold text-ginger-700 transition-colors hover:bg-ginger-300/50"
                    >
                      {initials}
                      <span
                        role="status"
                        title={t(`status.${status}`)}
                        className={cn(
                          'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-sidebar',
                          status === 'open' ? 'bg-mint-500' : status === 'offline' ? 'bg-red-600' : 'bg-amber-400'
                        )}
                      />
                    </button>
                  )}
                />
              </>
            ) : (
              <>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ginger-100 text-xs font-semibold text-ginger-700">{initials}</span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13.5px] font-medium text-ink-100">{user?.name}</div>
                  <div className="truncate text-xs text-ink-500">{user?.email}</div>
                </div>
                <Dropdown
                  label={t('nav.account')}
                  items={[
                    { label: t('settings.profile'), icon: <IconUser width={16} height={16} />, onSelect: () => navigate('/settings/profile') },
                    { label: t('auth.logout'), icon: <IconLogout width={16} height={16} />, onSelect: () => void session.logout(), divider: true }
                  ]}
                  trigger={p => (
                    <button {...p} ref={p.ref} type="button" aria-label={t('nav.account')} className="flex h-9 w-9 items-center justify-center rounded-[8px] text-ink-500 transition-colors hover:bg-cream-200 hover:text-ink-100">
                      <IconMore />
                    </button>
                  )}
                />
              </>
            )}
          </div>
        </div>
      </aside>
    )
  }

  return (
    <MobileBarCtx.Provider value={setBar}>
    <div className="flex h-[var(--app-h)]">
      <div className="hidden md:block">{sidebar(false)}</div>
      {open && (
        <div className="fixed inset-x-0 top-0 z-40 h-[var(--app-h)] md:hidden">
          <button
            type="button"
            tabIndex={-1}
            aria-label={t('common.close')}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              close()
            }}
            className="absolute inset-0 h-full w-full cursor-pointer border-0 bg-[rgb(20_20_18_/_0.4)] p-0 dark:bg-[rgb(0_0_0_/_0.55)]"
          />
          <div className="relative z-10 h-full w-fit shadow-[8px_0_32px_rgba(0,0,0,0.25)]">{sidebar(true)}</div>
        </div>
      )}
      <div id="app-content-col" className="relative flex min-w-0 flex-1 flex-col bg-canvas">
        <div className="flex h-[calc(3.5rem+env(safe-area-inset-top,0px))] shrink-0 items-center gap-1 border-b border-line bg-canvas pl-[calc(0.375rem+env(safe-area-inset-left,0px))] pr-[calc(0.375rem+env(safe-area-inset-right,0px))] pt-[env(safe-area-inset-top,0px)] md:hidden">
          <button type="button" aria-label={t('nav.menu')} onClick={() => setOpen(true)} className="flex h-11 w-11 items-center justify-center rounded-[10px] text-ink-100 hover:bg-cream-200">
            <IconMenu width={22} height={22} />
          </button>
          <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-ink-100">{bar?.title ?? title}</span>
          {bar?.actions && <div className="flex items-center">{bar.actions}</div>}
        </div>
        {status === 'offline' && <div role="status" className="bg-amber-50 px-4 py-1.5 text-center text-xs font-medium text-amber-700">{t('status.offlineBanner')}</div>}
        <main className="min-h-0 flex-1 overflow-y-auto bg-canvas"><Outlet /></main>
      </div>
      <ChatSearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
    </MobileBarCtx.Provider>
  )
}
